import React from 'react';
import PropTypes from 'prop-types';
import { subscribe } from 'react-contextual';
import { Route, Link } from 'react-router-dom';
import {
	Col, Row,
	Badge, Button, Collapse, Table,
	Form, FormGroup, Label, Input
} from 'reactstrap';
import format from 'date-fns/format';
import isBefore from 'date-fns/is_before';
import isAfter from 'date-fns/is_after';
import isEqual from 'date-fns/is_equal';
import compareAsc from 'date-fns/compare_asc';

import { ConfigContext } from 'contexts/config';

import { saveAs } from 'file-saver';

import { fetchChildren } from 'actions/child';
import { fetchSchoolYears, fetchPunctualContractsOfSchoolYear } from 'actions/school_year';
import {
	updatePunctualContract,
	removePunctualContract,
	addPunctualOfferToPunctualContract,
	removePunctualOfferFromPunctualContract,
	generatePunctualContractInvoice
} from 'actions/punctual_contract';
import {
	sendPunctualContractInvoice,
	fetchPunctualContractInvoice,
	removePunctualContractInvoice,
	updatePunctualContractInvoice,
	addPunctualContractInvoiceModification,
	removePunctualContractInvoiceModification
} from 'actions/punctual_contract_invoice';

import { debounce } from 'services/debounce';
import ConfirmationModal from 'components/utils/confirmation_modal';
import Paginate from 'components/utils/paginate';

import AdminPunctualContractDetail from 'components/admin/contracts/punctual_contract';

class AdminPunctualContracts extends React.Component {
	state = {
		dataReady: false,
		schoolYears: [],
		schoolYearsById: null,
		children: [],
		childrenById: null,
		punctualContracts: [],
		showFilters: false,
		filters: {
			remote: {
				schoolYearId: this.props.currentSchoolYearId,
			},
			local: {
				contractStatus: 'valid',
				invoiceStatus: 'any',
				minStartDate: '',
				maxStartDate: '',
				lastname: ''
			}
		},
		filteredPunctualContracts: [],
		paginationStartIndex: 0,
		paginationEndIndex: 0,
		paginationCurrentPage: 1,
		removeContractId: 0,
		removeContractModalOpen: false
	};

	updatePunctualContract = (punctualContractId, changes) => {
		return updatePunctualContract(punctualContractId, changes)
		.then((punctualContract) => {
			this.setState((prevState) => {
				const punctualContracts = prevState.punctualContracts.map((pc) => pc.id === punctualContractId ? { ...pc, ...punctualContract } : pc);
				return {
					punctualContracts,
					filteredPunctualContracts: this.filteredPunctualContracts(punctualContracts)
				};
			});
		});
	};

	generatePunctualContractInvoice = (punctualContractId) => {
		return generatePunctualContractInvoice(punctualContractId)
		.then((invoice) => {
			this.setState((prevState) => {
				const punctualContracts = prevState.punctualContracts.map((pc) => {
					return pc.id === punctualContractId ? {
						...pc,
						PunctualContractInvoice: invoice
					} : pc;
				});
				return {
					punctualContracts,
					filteredPunctualContracts: this.filteredPunctualContracts(punctualContracts)
				};
			});
		});
	};

	downloadPunctualContractInvoice = (invoiceId, filename) => {
		return fetchPunctualContractInvoice(invoiceId)
		.then((blob) => {
			saveAs(blob, filename);
		});
	};

	sendPunctualContractInvoice = (punctualContractId, invoiceId) => {
		return sendPunctualContractInvoice(invoiceId)
		.then((invoice) => {
			this.setState((prevState) => {
				const punctualContracts = prevState.punctualContracts.map((pc) => {
					return pc.id === punctualContractId ? {
						...pc,
						PunctualContractInvoice: { ...pc.PunctualContractInvoice, ...invoice }
					} : pc;
				});
				return {
					punctualContracts,
					filteredPunctualContracts: this.filteredPunctualContracts(punctualContracts)
				};
			});
		});
	};

	removePunctualContractInvoice = (punctualContractId, invoiceId) => {
		return removePunctualContractInvoice(invoiceId)
		.then(() => {
			this.setState((prevState) => {
				const punctualContracts = prevState.punctualContracts.map((pc) => {
					return pc.id === punctualContractId ? {
						...pc,
						PunctualContractInvoice: null
					} : pc;
				});
				return {
					punctualContracts,
					filteredPunctualContracts: this.filteredPunctualContracts(punctualContracts)
				};
			});
		});
	};

	updatePunctualContractInvoice = (punctualContractId, invoiceId, changes) => {
		return updatePunctualContractInvoice(invoiceId, changes)
		.then((invoice) => {
			this.setState((prevState) => {
				const punctualContracts = prevState.punctualContracts.map((pc) => {
					return pc.id === punctualContractId ? {
						...pc,
						PunctualContractInvoice: { ...pc.PunctualContractInvoice, ...invoice }
					} : pc;
				});
				return {
					punctualContracts,
					filteredPunctualContracts: this.filteredPunctualContracts(punctualContracts)
				};
			});
		});
	};

	addPunctualContractInvoiceModification = (punctualContractId, invoiceId, modif) => {
		return addPunctualContractInvoiceModification(invoiceId, modif)
		.then((invoiceModification) => {
			this.setState((prevState) => ({
				punctualContracts: prevState.punctualContracts.map((pc) => {
					return pc.id === punctualContractId ? {
						...pc,
						PunctualContractInvoice: {
							...pc.PunctualContractInvoice,
							InvoiceModifications: [ ...pc.PunctualContractInvoice.InvoiceModifications, invoiceModification ]
						}
					} : pc;
				})
			}));
		});
	};

	removePunctualContractInvoiceModification = (punctualContractId, invoiceId, modifId) => {
		return removePunctualContractInvoiceModification(invoiceId, modifId)
		.then(() => {
			this.setState((prevState) => ({
				punctualContracts: prevState.punctualContracts.map((pc) => {
					return pc.id === punctualContractId ? {
						...pc,
						PunctualContractInvoice: {
							...pc.PunctualContractInvoice,
							InvoiceModifications: pc.PunctualContractInvoice.InvoiceModifications.filter((m) => m.id !== modifId)
						}
					} : pc;
				})
			}));
		});
	};

	addPunctualOfferToPunctualContract = (punctualContractId, punctualOffer) => {
		return addPunctualOfferToPunctualContract(punctualContractId, punctualOffer)
		.then((punctualContract) => {
			this.setState((prevState) => {
				const punctualContracts = prevState.punctualContracts.map((pc) => pc.id === punctualContractId ? { ...pc, ...punctualContract } : pc);
				return {
					punctualContracts,
					filteredPunctualContracts: this.filteredPunctualContracts(punctualContracts)
				}
			});
		});
	};

	removePunctualOfferFromPunctualContract = (punctualContractId, punctualOfferId) => {
		return removePunctualOfferFromPunctualContract(punctualContractId, punctualOfferId)
		.then((punctualContract) => {
			this.setState((prevState) => {
				const punctualContracts = prevState.punctualContracts.map((pc) => pc.id === punctualContractId ? { ...pc, ...punctualContract } : pc);
				return {
					punctualContracts,
					filteredPunctualContracts: this.filteredPunctualContracts(punctualContracts)
				}
			});
		});
	};

	handleRemovePunctualContract = (punctualContractId) => {
		return removePunctualContract(punctualContractId)
		.then(() => {
			this.setState((prevState) => {
				const punctualContracts = prevState.punctualContracts.filter((c) => c.id !== punctualContractId);
				return {
					punctualContracts,
					filteredPunctualContracts: this.filteredPunctualContracts(punctualContracts)
				};
			});
		});
	};
	
	toggleFilters = () => {
		this.setState((prevState) => ({ showFilters: !prevState.showFilters }));
	};
	
	handleFilterChange = (value, prop) => {
		if (prop === 'schoolYearId') { // remote filter
			this.setState((prevState) => ({
				dataReady: false,
				filters: {
					remote: {
						...prevState.filters.remote,
						[prop]: value,
					},
					local: {
						...prevState.filters.local,
						minStartDate: this.state.schoolYearsById[value].startDate,
						maxStartDate: this.state.schoolYearsById[value].endDate
					}
				}
			}), () => {
				fetchPunctualContractsOfSchoolYear(this.state.filters.remote.schoolYearId)
				.then((punctualContracts) => {
					this.setState({
						punctualContracts,
						dataReady: true,
						filteredPunctualContracts: this.filteredPunctualContracts(punctualContracts)
					});
				});
			});
		} else { // local filter
			this.setState((prevState) => ({
				filters: {
					remote: { ...prevState.filters.remote },
					local: {
						...prevState.filters.local,
						[prop]: value
					}
				}
			}), this.debouncedUpdateFilteredPunctualContracts);
		}
	};

	filteredPunctualContracts = (punctualContracts) => {
		return punctualContracts
		.filter((punctualContract) => {
			const child = this.state.childrenById[punctualContract.ChildId];
			const invoice = punctualContract.PunctualContractInvoice;
			punctualContract.startDate = punctualContract.PunctualOffers.length > 0 ?
				punctualContract.PunctualOffers.reduce(
					(start, offer) => isBefore(offer.Day.date, start) ? offer.Day.date : start,
					punctualContract.PunctualOffers[0].Day.date
				) :
				this.state.schoolYearsById[this.state.filters.remote.schoolYearId].startDate;

			return child.lastname.toLowerCase().includes(this.state.filters.local.lastname.toLowerCase()) &&
				(this.state.filters.local.contractStatus === 'any' || punctualContract.status === this.state.filters.local.contractStatus) &&
				(this.state.filters.local.invoiceStatus === 'any' || (!invoice && this.state.filters.local.invoiceStatus === 'not edited') || (invoice && invoice.status === this.state.filters.local.invoiceStatus)) &&
				(isAfter(punctualContract.startDate, this.state.filters.local.minStartDate) || isEqual(punctualContract.startDate, this.state.filters.local.minStartDate)) &&
				isBefore(punctualContract.startDate, this.state.filters.local.maxStartDate);
		})
		.sort((a, b) => compareAsc(a.startDate, b.startDate));
	};
	updateFilteredPunctualContracts = () => {
		this.setState({
			filteredPunctualContracts: this.filteredPunctualContracts(this.state.punctualContracts)
		});
	};
	debouncedUpdateFilteredPunctualContracts = debounce(this.updateFilteredPunctualContracts, 500, false);

	pageChanged = (paginationStartIndex, paginationEndIndex, paginationCurrentPage) => {
		this.setState({ paginationStartIndex, paginationEndIndex, paginationCurrentPage });
	};

	componentDidMount() {
		Promise.all([
			fetchSchoolYears(),
			fetchChildren(),
			fetchPunctualContractsOfSchoolYear(this.state.filters.remote.schoolYearId)
		])
		.then(([ schoolYears, children, punctualContracts ]) => {
			const schoolYearsById = {};
			schoolYears.forEach((s) => schoolYearsById[s.id] = s);
			const childrenById = {};
			children.forEach((c) => childrenById[c.id] = c);
			this.setState((prevState) => ({
				schoolYears,
				schoolYearsById,
				children,
				childrenById,
				punctualContracts,
				filters: {
					remote: { ...prevState.filters.remote, },
					local: {
						...prevState.filters.local,
						minStartDate: schoolYearsById[this.props.currentSchoolYearId].startDate,
						maxStartDate: schoolYearsById[this.props.currentSchoolYearId].endDate
					}
				}
			}), () => {
				this.setState({
					dataReady: true,
					filteredPunctualContracts: this.filteredPunctualContracts(this.state.punctualContracts)
				});
			});
		});
	}

	render() {
		const filterParagraphStyle = {
			color: 'white',
			backgroundColor: 'rgba(52, 58, 64, 0.60)',
			cursor: 'pointer'
		};
		return (
			!this.state.dataReady ?
			'Loading...' :
			<React.Fragment>
			<Row>
				<Col>
					<p style={ filterParagraphStyle } className="p-2 d-flex align-items-center" onClick={ this.toggleFilters }>
						Filters
						<Badge className="ml-3" color="info">{ this.state.filteredPunctualContracts.length }</Badge>
						{ this.state.showFilters ? <i className="fa fa-minus-square-o ml-auto"></i> : <i className="fa fa-plus-square-o ml-auto"></i> }
					</p>
					<Collapse isOpen={ this.state.showFilters }>
						<Form onSubmit={ (e) => e.preventDefault() }>
							<FormGroup row>
								<Label for="schoolYear" sm={2}>School year</Label>
								<Col sm={4}>
									<Input type="select" value={ this.state.filters.remote.schoolYearId } onChange={ (e) => this.handleFilterChange(e.target.value, 'schoolYearId') } name="schoolYear" id="schoolYear">
										{ this.state.schoolYears.map((schoolYear) => (
											<option key={ schoolYear.id } value={ parseInt(schoolYear.id, 10) }>{ schoolYear.title }</option>
										)) }
									</Input>
								</Col>
								<Label for="lastname" sm={2}>Lastname</Label>
								<Col sm={4}>
									<Input type="text" value={ this.state.filters.local.lastname } onChange={ (e) => this.handleFilterChange(e.target.value, 'lastname') } name="lastname" id="lastname" placeholder="Lastname" />
								</Col>
							</FormGroup>
							<FormGroup row>
								<Label for="contractStatus" sm={2}>Contract status</Label>
								<Col sm={4}>
									<Input type="select" className="fa" value={ this.state.filters.local.contractStatus } onChange={ (e) => this.handleFilterChange(e.target.value, 'contractStatus') } name="contractStatus" id="contractStatus">
										<option value="any">-- any --</option>
										<option value="valid">valid</option>
										<option value="demand">demand</option>
									</Input>
								</Col>
								<Label for="invoiceStatus" sm={2}>Invoice status</Label>
								<Col sm={4}>
									<Input type="select" className="fa" value={ this.state.filters.local.invoiceStatus } onChange={ (e) => this.handleFilterChange(e.target.value, 'invoiceStatus') } name="invoiceStatus" id="invoiceStatus">
										<option value="any">-- any --</option>
										<option value="not edited">not edited</option>
										<option value="edited">edited</option>
										<option value="sent">sent</option>
										<option value="paid">paid</option>
									</Input>
								</Col>
							</FormGroup>
							<FormGroup row>
								<Label for="minStartDate" sm={2}>Start date between</Label>
								<Col sm={2}>
									<Input type="date"
										min={ this.state.schoolYearsById[this.state.filters.remote.schoolYearId].startDate }
										max={ this.state.schoolYearsById[this.state.filters.remote.schoolYearId].endDate }
										value={ this.state.filters.local.minStartDate }
										onChange={ (e) => this.handleFilterChange(e.target.value, 'minStartDate') } name="minStartDate" id="minStartDate"
									/>
								</Col>
								<Col sm={2}>
									<Input type="date" 
										min={ this.state.schoolYearsById[this.state.filters.remote.schoolYearId].startDate }
										max={ this.state.schoolYearsById[this.state.filters.remote.schoolYearId].endDate }
										value={ this.state.filters.local.maxStartDate }
										onChange={ (e) => this.handleFilterChange(e.target.value, 'maxStartDate') } name="maxStartDate" id="maxStartDate"
									/>
								</Col>
							</FormGroup>
						</Form>
						<hr />
					</Collapse>
				</Col>
			</Row>
			<Row>
				<Col>
					<Route path={ this.props.match.url } exact render={ () => (
						<React.Fragment>
						<Paginate nbItems={ this.state.filteredPunctualContracts.length } onPageChange={ this.pageChanged } initialPage={ this.state.paginationCurrentPage } />
						<Table striped hover size="sm">
							<thead>
								<tr>
									<th>Child</th>
									<th>Start date</th>
									<th>End date</th>
									<th>Demande date</th>
									<th>Contract status</th>
									<th>Invoice status</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
							{ this.state.filteredPunctualContracts.slice(this.state.paginationStartIndex, this.state.paginationEndIndex + 1).map((punctualContract) => {
								const child = this.state.childrenById[punctualContract.ChildId];
								const invoice = punctualContract.PunctualContractInvoice;
								const startDate = punctualContract.PunctualOffers.length > 0 ?
									format(punctualContract.PunctualOffers.reduce(
										(start, offer) => isBefore(offer.Day.date, start) ? offer.Day.date : start,
										punctualContract.PunctualOffers[0].Day.date
									), 'DD-MM-YYYY') :
									'???';
								const endDate = punctualContract.PunctualOffers.length > 0 ?
									format(punctualContract.PunctualOffers.reduce(
										(end, offer) => isAfter(offer.Day.date, end) ? offer.Day.date : end,
										punctualContract.PunctualOffers[0].Day.date
									), 'DD-MM-YYYY') :
									'???';
								
								return (
									<tr key={ punctualContract.id }>
										<td>
											{ child.firstname } { child.lastname }
											<Link to={ '/admin/children/' + child.id + '/info' } className="ml-2"><i className="fa fa-external-link"></i></Link>
										</td>
										<td>{ startDate }</td>
										<td>{ endDate }</td>
										<td>{ format(punctualContract.createdAt, 'DD-MM-YYYY') }</td>
										<td>
											{ punctualContract.status === 'valid' ?
												<i style={{ color: 'green' }} className="fa fa-check"></i> :
												<i style={{ color: 'red' }} className="fa fa-ban"></i>
											}
										</td>
										<td>
											{
												invoice && invoice.status === 'edited' ?
												<React.Fragment><i style={{ color: '#FF8000' }} className="fa fa-file-text"></i>{' '}<span>edited</span></React.Fragment> :
												invoice && invoice.status === 'sent' ?
												<React.Fragment><i style={{ color: '#CCCC00' }} className="fa fa-envelope"></i>{' '}<span>sent</span></React.Fragment> :
												invoice && invoice.status === 'paid' ?
												<React.Fragment><i style={{ color: '#66CC00' }} className="fa fa-money"></i>{' '}<span>paid</span></React.Fragment> :
												<React.Fragment><i style={{ color: '#CC0000' }} className="fa fa-ban"></i>{' '}<span>not edited</span></React.Fragment>
											}
										</td>
										<td className="text-right">
											{ punctualContract.status === 'demand' &&
											<Button size="sm" color="danger" className="ml-2" style={{ cursor: 'pointer' }} onClick={ () => this.setState({ removeContractId: punctualContract.id, removeContractModalOpen: true }) }>
												<i className="fa fa-trash"></i>
											</Button> }
											<Button size="sm" color="info" className="ml-2" tag={ Link } to={ this.props.match.url + '/' + punctualContract.id }>
												<i className="fa fa-search"></i>
											</Button>
										</td>
									</tr>
								);
							}) }
							</tbody>
						</Table>
						<ConfirmationModal
							isOpen={ this.state.removeContractModalOpen }
							text="Are you sure you want to remove this contract ?"
							onConfirm={ () => { this.handleRemovePunctualContract(this.state.removeContractId); this.setState({ removeContractId: 0, removeContractModalOpen: false }); } }
							onDismiss={ () => { this.setState({ removeContractId: 0, removeContractModalOpen: false }); } }
						/>
						</React.Fragment>
					) } />
					<Route path={ this.props.match.url + '/:contractId' } render={ (props) => {
						const contract = this.state.punctualContracts.find((c) => c.id === parseInt(props.match.params.contractId, 10));
						const child = this.state.childrenById[contract.ChildId];
						return (
							<React.Fragment>
								<Row>
									<Col>
										<h3 style={{ display: 'inline' }}>{ child.firstname } { child.lastname }</h3>
										<Button style={{ verticalAlign: 'top' }} size="sm" color="info" className="ml-2" tag={ Link } to={ this.props.match.url }>
											<i className="fa fa-list"></i>{' '}Back to list
										</Button>
									</Col>
								</Row>
								<AdminPunctualContractDetail
									punctualContract={ contract }
									updatePunctualContract={ (changes) => this.updatePunctualContract(contract.id, changes) }
									generatePunctualContractInvoice={ () => this.generatePunctualContractInvoice(contract.id) }
									removePunctualContractInvoice={ (invoiceId) => this.removePunctualContractInvoice(contract.id, invoiceId) }
									downloadPunctualContractInvoice={ (invoiceId, filename) => this.downloadPunctualContractInvoice(invoiceId, filename) }
									sendPunctualContractInvoice={ (invoiceId) => this.sendPunctualContractInvoice(contract.id, invoiceId) }
									updatePunctualContractInvoice={ (invoiceId, changes) => this.updatePunctualContractInvoice(contract.id, invoiceId, changes) }
									addOfferToContract={ (offer) => this.addPunctualOfferToPunctualContract(contract.id, offer) }
									removeOfferFromContract={ (offerId) => this.removePunctualOfferFromPunctualContract(contract.id, offerId) }
									addPunctualContractInvoiceModification={ (invoiceId, modif) => this.addPunctualContractInvoiceModification(contract.id, invoiceId, modif) }
									removePunctualContractInvoiceModification={ (invoiceId, modifId) => this.removePunctualContractInvoiceModification(contract.id, invoiceId, modifId) }
								/>
							</React.Fragment>
						);
					 } } />
				</Col>
			</Row>
			</React.Fragment>
		);
	}
};

AdminPunctualContracts.propTypes = {
	currentSchoolYearId: PropTypes.number.isRequired
};

export default subscribe(
	[ ConfigContext ],
	(config) => ({
		currentSchoolYearId: config.getConfigValue('currentSchoolYearId')
	})
)(AdminPunctualContracts);
